import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
const secondaryNav = [
  { label: "Services", href: "/services" },
  { label: "Work", href: "/work" },
  { label: "Book", href: "/book" },
  { label: "FAQ", href: "/faq" },
];

const socialLinks = [
  { label: "Instagram", href: "https://www.instagram.com/lets.face.it.makeup/" },
];

const legalLinks = [
  { label: "Privacy", href: "/privacy" },
  { label: "Terms", href: "/terms" },
  { label: "Copyright", href: "/copyright" },
];

export default function Footer() {
  const [isCompactView, setIsCompactView] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isVerySmallScreen, setIsVerySmallScreen] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const mediaQuery = window.matchMedia("(max-width: 934px)");
    const smallScreenQuery = window.matchMedia("(max-width: 403px)");
    
    const updateView = (matches: boolean) => {
      setIsCompactView(matches);
      if (!matches) setIsExpanded(false);
    };

    const updateSmallScreen = (matches: boolean) => {
      setIsVerySmallScreen(matches);
    };

    updateView(mediaQuery.matches);
    updateSmallScreen(smallScreenQuery.matches);
    
    const handleChange = (event: MediaQueryListEvent) => updateView(event.matches);
    const handleSmallScreenChange = (event: MediaQueryListEvent) => updateSmallScreen(event.matches);
    
    mediaQuery.addEventListener("change", handleChange);
    smallScreenQuery.addEventListener("change", handleSmallScreenChange);

    return () => {
      mediaQuery.removeEventListener("change", handleChange);
      smallScreenQuery.removeEventListener("change", handleSmallScreenChange);
    };
  }, []);

  const handleToggleClick = () => {
    if (!isCompactView) return;
    setIsExpanded((prev) => !prev);
  };

  const handleLinkClick = () => {
    if (isCompactView && isExpanded) {
      setIsExpanded(false);
    }
  };

  const cueLabel = isExpanded 
    ? (isVerySmallScreen ? "Hide" : "Hide")
    : (isVerySmallScreen ? "More" : "More");

  return (
    <footer
      className={`fixed bottom-0 inset-x-0 z-30 w-full bg-black text-zinc-200 transition-all
        duration-300 ease-out h-[38px] max-[934px]:h-auto max-[934px]:overflow-hidden max-[934px]:cursor-default ${
        isExpanded ? "max-[934px]:max-h-[85vh]" : "max-[934px]:max-h-9"
      }`}
      aria-label="Site footer"
    >
      <div
        id="footer-details"
        className={`w-full px-3 sm:px-6 lg:px-20 flex items-center justify-between gap-3 text-[16px] md:text-[17px]
         text-zinc-200 overflow-x-auto min-[935px]:h-full
         max-[934px]:flex-col max-[934px]:items-start max-[934px]:overflow-visible transition-all duration-300 ease-out
         ${isExpanded ? 'max-[934px]:pt-4 max-[934px]:pb-6 max-[934px]:gap-5' : 'max-[934px]:pt-[7px] max-[934px]:pb-[1.5px]'}`}
      >
        {/* LEFT: Secondary navigation (stays left) */}
        <nav
          className="flex items-center gap-2 sm:gap-3 
            max-[934px]:w-full max-[934px]:items-start max-[934px]:min-h-5"
          aria-label="Secondary navigation"
        >
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 max-[934px]:flex-1 max-[934px]:gap-x-3 max-[934px]:gap-y-1">
            {secondaryNav.map((item) => (
              <Link
                key={item.label}
                to={item.href}
                className="hover:text-[#d4b896] transition-colors"
                onClick={handleLinkClick}
              >
                {item.label}
              </Link>
            ))}
          </div>

          {isCompactView && (
            <button
              type="button"
              onClick={handleToggleClick}
              aria-expanded={isExpanded}
              aria-controls="footer-details"
              aria-label={cueLabel}
              className="ml-auto hidden max-[934px]:inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px]
                uppercase tracking-[0.2em] text-white/70 transition-colors hover:text-[#d4b896] focus-visible:outline-none whitespace-nowrap shrink-0 max-[403px]:normal-case"
            >
              <span className="flex items-center gap-2">
                {cueLabel}
                <svg
                  className={`h-3 w-3 transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`}
                  viewBox="0 0 14 8"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path d="M1 1.5L7 6.5L13 1.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                </svg>
              </span>
            </button>
          )}
        </nav>

        {/* MIDDLE-LEFT: Instagram (its own flex item) */}
        <div className="flex items-center max-[934px]:w-full max-[934px]:py-2 max-[934px]:border-t max-[934px]:border-white/10">
          {socialLinks.map((item) => (
            <a
              key={item.label}
              href={item.href}
              target="_blank"
              rel="noreferrer"
              className="hover:text-[#d4b896] transition-colors max-[934px]:py-1"
              onClick={handleLinkClick}
            >
              {item.label}
            </a>
          ))}
        </div>

        {/* MIDDLE-RIGHT: Book a session (its own flex item) */}
        <Link
          to="/book"
          className="whitespace-nowrap rounded-none px-2 md:px-3 py-0.5 text-[17px]
            tracking-wide hover:text-[#d4b896] transition-colors
            max-[934px]:w-full max-[934px]:border max-[934px]:border-white/20 max-[934px]:rounded-md
            max-[934px]:py-3 max-[934px]:text-center max-[934px]:my-2 max-[934px]:text-[18px]"
          onClick={handleLinkClick}
        >
          Book a Session
        </Link>

        {/* RIGHT: Legal + copyright (stays right) */}
        <div
          className="flex items-center gap-2.5 text-[16px] 
            max-[934px]:flex-wrap max-[934px]:gap-3 max-[934px]:w-full max-[934px]:pt-3 max-[934px]:border-t max-[934px]:border-white/10"
          aria-label="Legal links"
        >
          <span className="hidden sm:inline">
            © {new Date().getFullYear()} Let&apos;s Face It Studio&nbsp;Atlanta, GA&nbsp;&nbsp;&nbsp;&nbsp;
          </span>

          {legalLinks.map((item) => (
            <Link
              key={item.label}
              to={item.href}
              className="hover:text-[#d4b896] transition-colors max-[934px]:py-1"
              onClick={handleLinkClick}
            >
              {item.label}
            </Link>
          ))}
        </div>
      </div>
    </footer>
  );
}